import { CommandBuffer } from "../commands/CommandBuffer";
import type { CompiledLevel, ContentCatalog } from "../content/types";
import { EventQueue } from "../events/EventQueue";
import {
  DropStore,
  ParticleStore,
  RecordingEntityIds,
  SlimeStore,
} from "../stores/EntityStores";
import { DeterministicRandom } from "./Random";
import { SpatialHashGrid } from "./SpatialHashGrid";
import type { GamePhase } from "./types";

export interface InputState {
  pointerX: number;
  pointerY: number;
  firing: boolean;
}
export interface GameMetrics {
  chainPops: number;
  shotsFired: number;
  /** 本局爆开的史莱姆数、玩家灌到临界引爆的次数（不含连锁）、爆开时的最大环宽。平均连锁 C = kills / playerBursts。 */
  kills: number;
  playerBursts: number;
  maxRing: number;
}
/** 投放规则（core:spawn-wave）的可变状态。有状态规则的契约见数据结构文档。 */
export interface SpawnState {
  /** 速率累积器：每步加 rate × dt，每满 1 投放一只。 */
  accumulator: number;
  /** 当前簇的落点与剩余个数。簇是「连着几个落在同一处」，用完再抽下一处。 */
  clusterX: number;
  clusterLeft: number;
  /** 当前波次下标，波间空档为 -1。 */
  waveIndex: number;
  /** 全部波次已跑完。通关判定读它。 */
  finished: boolean;
  /** 因容量已满被拒绝的投放次数。诊断用，暂无外部读取点以外的消费者。 */
  refused: number;
}
export interface GameState {
  phase: GamePhase;
  levelIndex: number;
  score: number;
  combo: number;
  comboTimer: number;
  playerX: number;
  targetPlayerX: number;
  fireCooldown: number;
  hitstop: number;
  slowMotion: number;
  shake: number;
  levelTime: number;
  /** 本次尝试累计漏网数（越过红线的史莱姆只计一次）。core:leak-budget 写、HUD 读。 */
  leaked: number;
  /** 空场提速倍率，乘在重力上。恒为 1 直到 core:empty-field-pace（T048 批次 C）写它。 */
  paceMultiplier: number;
  /** 开发期场景开关：暂停投放（波次时间照走、只是不投）、落速倍率覆盖（0 即冻结）。生产下恒为默认值。 */
  spawnPaused: boolean;
  fallOverride: number;
  /** 本次尝试的随机种子。与参数指纹、输入流共同构成完整初始条件（决策 0017）。 */
  seed: number;
  /** 自本次尝试开始的固定步计数。毫秒对不上固定步，回放要按步定位参数变更（决策 0017）。 */
  steps: number;
  testMode: boolean;
}

export class GameContext {
  readonly slimes: SlimeStore;
  readonly drops: DropStore;
  readonly particles = new ParticleStore();
  readonly events = new EventQueue();
  readonly commands = new CommandBuffer();
  readonly grid: SpatialHashGrid;
  readonly input: InputState;
  readonly metrics: GameMetrics = {
    chainPops: 0,
    shotsFired: 0,
    kills: 0,
    playerBursts: 0,
    maxRing: 0,
  };
  readonly spawn: SpawnState = {
    accumulator: 0,
    clusterX: 0,
    clusterLeft: 0,
    waveIndex: -1,
    finished: false,
    refused: 0,
  };
  readonly state: GameState;
  /** 玩法随机的唯一来源。禁止在玩法路径上使用 Math.random——回放校验依赖可复现。 */
  readonly random = new DeterministicRandom(1);
  level!: CompiledLevel;

  constructor(readonly catalog: ContentCatalog) {
    const world = catalog.levels[0].world;
    const recordingIds = new RecordingEntityIds();
    this.slimes = new SlimeStore(catalog, recordingIds);
    this.drops = new DropStore(catalog, recordingIds);
    this.grid = new SpatialHashGrid(world.width, world.height);
    this.input = { pointerX: world.width / 2, pointerY: 160, firing: false };
    this.state = {
      phase: "playing",
      levelIndex: 0,
      score: 0,
      combo: 0,
      comboTimer: 0,
      playerX: world.width / 2,
      targetPlayerX: world.width / 2,
      fireCooldown: 0,
      hitstop: 0,
      slowMotion: 0,
      shake: 0,
      levelTime: 0,
      leaked: 0,
      paceMultiplier: 1,
      spawnPaused: false,
      fallOverride: 1,
      steps: 0,
      seed: 1,
      testMode: false,
    };
  }

  /** 开始一次新尝试：换种子并复位随机源。测试模式用固定种子以保证用例可复现。 */
  startAttempt(): void {
    this.state.seed = this.state.testMode
      ? 1
      : crypto.getRandomValues(new Uint32Array(1))[0];
    this.random.reset(this.state.seed);
  }

  resetTransientState(): void {
    this.drops.clear();
    this.particles.clear();
    this.events.clear();
    this.commands.clear();
    Object.assign(this.state, {
      phase: "playing",
      combo: 0,
      comboTimer: 0,
      fireCooldown: 0,
      hitstop: 0,
      slowMotion: 0,
      shake: 0,
      levelTime: 0,
      leaked: 0,
      paceMultiplier: 1,
      spawnPaused: false,
      fallOverride: 1,
      steps: 0,
    });
    Object.assign(this.spawn, {
      accumulator: 0,
      clusterX: 0,
      clusterLeft: 0,
      waveIndex: -1,
      finished: false,
      refused: 0,
    });
    // 种子不在此重置：换关卡与重开由 startAttempt 显式决定，避免重置语义分散在两处。
    Object.assign(this.input, { firing: false });
  }
}
